import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, LogIn, Flag, History, Trophy, LayoutDashboard, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, homeFor, EVENT_STATUS_CLS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Logo, useSettings, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";

const HERO = "/images/hero-kart.jpg";
const MONTHS = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

function DateBlock({ date, muted }) {
  const [, m, d] = date.split("-");
  return (
    <div className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl ${muted ? "bg-slate-100 text-slate-500" : "bg-[#0F2042] text-white"}`}>
      <span className="font-display text-2xl font-black leading-none">{d}</span>
      <span className={`mt-1 text-[10px] font-bold tracking-widest ${muted ? "" : "text-amber-400"}`}>{MONTHS[Number(m) - 1]}</span>
    </div>
  );
}

function EventRow({ e, past, i }) {
  const range = e.end_date && e.end_date !== e.start_date ? `${fmtDate(e.start_date)} – ${fmtDate(e.end_date)}` : fmtDate(e.start_date);
  return (
    <article className="fade-up flex gap-4 rounded-2xl border border-slate-200 bg-white p-4 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md sm:p-5"
      style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`public-event-${e.id}`}>
      <DateBlock date={e.start_date} muted={past} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-tight text-slate-900" data-testid={`public-event-name-${e.id}`}>{e.name}</h3>
          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${EVENT_STATUS_CLS[e.status] || ""}`} data-testid={`public-event-status-${e.id}`}>{e.status}</span>
        </div>
        <div className="mt-1 text-sm font-medium text-blue-800">{e.discipline_name} · {e.sector_name}</div>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
          <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-slate-400" />{range}</span>
          {e.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{e.location}</span>}
          {e.club && <span className="flex items-center gap-1.5"><Flag className="h-4 w-4 text-slate-400" />{e.club}</span>}
        </div>
        {e.categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {e.categories.map((c) => <span key={c} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{c}</span>)}
          </div>
        )}
      </div>
    </article>
  );
}

function Section({ icon: Icon, title, events, past, empty, testid }) {
  return (
    <section data-testid={testid}>
      <div className="mb-4 flex items-center gap-2">
        <Icon className={`h-5 w-5 ${past ? "text-slate-400" : "text-amber-500"}`} />
        <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">{title}</h2>
        <span className="text-sm text-slate-400" data-testid={`${testid}-count`}>({events.length})</span>
      </div>
      <div className="space-y-3">
        {events.length ? events.map((e, i) => <EventRow key={e.id} e={e} past={past} i={i} />)
          : <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">{empty}</div>}
      </div>
    </section>
  );
}

function Header() {
  const { user } = useAuth();
  const settings = useSettings();
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0F2042]/95 text-white backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-3" data-testid="public-home-brand">
          <Logo settings={settings} dark className="h-10 w-10" />
          <div className="hidden min-w-0 leading-tight sm:block">
            <div className="font-display text-base font-bold">Passrace</div>
            <div className="truncate text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400">MSP Italia<span className="hidden sm:inline"> · Motori</span></div>
          </div>
        </Link>
        <div className="flex shrink-0 items-center gap-2">
          <Button asChild variant="outline" className="rounded-full border-white/30 bg-white/5 px-4 font-semibold text-white hover:bg-white/15 hover:text-white" data-testid="public-home-verify-btn">
            <Link to="/verifica"><ShieldCheck className="mr-2 h-4 w-4" /><span>Verifica<span className="hidden sm:inline"> licenza</span></span></Link>
          </Button>
          <Button asChild className="rounded-full bg-amber-500 px-4 font-semibold text-[#0F2042] hover:bg-amber-400" data-testid="public-home-login-btn">
            {user ? <Link to={homeFor(user.role)}><LayoutDashboard className="mr-2 h-4 w-4" />La mia area</Link>
              : <Link to="/login"><LogIn className="mr-2 h-4 w-4" />Area riservata</Link>}
          </Button>
        </div>
      </div>
    </header>
  );
}

export default function PublicHome() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/events/public/list").then((r) => setData(r.data)).catch(() => setData({ upcoming: [], past: [] })); }, []);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-home">
      <Header />
      <div className="relative overflow-hidden bg-[#0F2042] text-white">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2042] via-[#0F2042]/85 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="mb-4 h-1 w-16 bg-amber-500" />
          <h1 className="fade-up max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">Calendario gare</h1>
          <p className="mt-4 max-w-xl text-base text-blue-100/80 md:text-lg">Le manifestazioni dei settori motori MSP Italia: karting e automobilismo.</p>
        </div>
      </div>
      <main className="mx-auto grid max-w-6xl gap-12 px-4 py-10 sm:px-6 lg:grid-cols-[1.4fr_1fr] lg:gap-10">
        {!data ? <Loading /> : (
          <>
            <Section icon={Trophy} title="Prossime manifestazioni" events={data.upcoming} testid="public-upcoming" empty="Nessuna manifestazione in programma al momento." />
            <Section icon={History} title="Manifestazioni passate" events={data.past} past testid="public-past" empty="Nessuna manifestazione conclusa." />
          </>
        )}
      </main>
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">Passrace · Registro Nazionale Licenze MSP Italia – Settori Motori</footer>
    </div>
  );
}
